import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { ITypeRichTextFields, transformCloudinaryUrl } from '@adac-mp/shared';
import React from 'react';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { Typography } from '../Typography';
import { Button } from '../Button';
import { BestPriceIcon } from '../../Icons';

export type RichtextProps = {
	document: ITypeRichTextFields;
	replaceText?: string;
	linkTrackingLabel?: string;
	checkmarkList?: boolean;
};

const options = (links, replaceText?, linkTrackingLabel?, checkmarkList?) => {
	const entryMap = new Map();

	// loop through the assets and add them to the map
	if (links) {
		// loop through the block linked entries and add them to the map
		for (const entry of links.entries?.block) {
			entryMap.set(entry.sys.id, entry);
		}
	}

	return {
		renderNode: {
			[BLOCKS.PARAGRAPH]: (_node, children) => children,
			[BLOCKS.HEADING_6]: (_node, children) => (
				<Typography component="p" size="xlSpace" className="pt-4 first-of-type:pt-0">
					{children}
				</Typography>
			),
			[BLOCKS.HEADING_1]: (_node, children) => (
				<Typography
					component="h1"
					size="h1"
					variant="header"
					color="title"
					className="font-normal pb-6 md:pb-8">
					{children}
				</Typography>
			),
			[BLOCKS.HEADING_2]: (_node, children) => (
				<Typography component="h2" size="h2" color="title" className="font-normal pb-6 md:pb-8">
					{children}
				</Typography>
			),
			[BLOCKS.HEADING_3]: (_node, children) => (
				<Typography component="h3" size="h3" color="title">
					{children}
				</Typography>
			),
			[BLOCKS.EMBEDDED_ENTRY]: (node) => {
				const entry = entryMap.get(node.data.target.sys.id);

				if (entry.__typename === 'Picture') {
					return (
						<img
							src={transformCloudinaryUrl(entry.data[0].secure_url)}
							alt={entry.data[0].alt}
							width="100%"
							height="auto"
							className="mt-4"
						/>
					);
				}
				return null;
			},
			[BLOCKS.UL_LIST]: (_node, children) => {
				return (
					<ul
						className={`${
							checkmarkList
								? 'flex flex-col gap-3 items-center justify-center md:flex-row md:gap-4.5'
								: 'pl-4'
						}`}>
						{children}
					</ul>
				);
			},
			[BLOCKS.LIST_ITEM]: (_node, children) =>
				checkmarkList ? (
					<li className="inline-flex items-center gap-1.5 list-none">
						<BestPriceIcon bold className="shrink-0" size="24" />
						<Typography color="title" size="lg">
							{children}
						</Typography>
					</li>
				) : (
					<li>{children}</li>
				),
			[INLINES.HYPERLINK]: (_node, children) => (
				<Button
					underline
					href={_node.data.uri}
					target={`${_node.data.uri.startsWith(_node.website_url) ? '_self' : '_blank'}`}
					variant="tertiary"
					size="sm"
					style={{ padding: 0, display: 'inline' }}
					tracking={
						linkTrackingLabel
							? {
									link_click: {
										label: `${linkTrackingLabel}_${_node.data.uri}`,
										element: 'Checkout',
									},
							  }
							: undefined
					}>
					{children}
				</Button>
			),
		},
		renderText: (text) => {
			if (replaceText) {
				text = text.replace('*', replaceText);
			}
			return text.split('\n').flatMap((textItem, i) => [i > 0 && <br key={i} />, textItem]);
		},
	};
};

export const Richtext: React.FC<RichtextProps> = ({ document, replaceText, linkTrackingLabel, checkmarkList }) =>
	documentToReactComponents(document.json, options(document.links, replaceText, linkTrackingLabel, checkmarkList));

export type RichtextElementType = typeof Richtext;
